import { isE164 } from '@lib/contacts/utils/phone';
import { z } from 'zod';

const STORAGE_KEY = 'portal:signup-phone-handoff';

const signupPhoneHandoffSchema = z.object({
  phone: z.string().refine(value => isE164(value)),
  smsOptIn: z.literal(true),
});

export type SignupPhoneHandoff = z.infer<typeof signupPhoneHandoffSchema>;

/**
 * Carries the number typed on /signup to the onboarding phone
 * step, which owns SMS verification. A signed-in user cannot
 * stay on /signup: Clerk runs `router.refresh()` after
 * `setActive`, and the middleware answers that request for
 * an auth page with a redirect to /home. sessionStorage keeps
 * the number out of the URL and dies with the tab.
 */
export function storeSignupPhoneHandoff(handoff: SignupPhoneHandoff): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(handoff));
  } catch {
    // Storage unavailable (private mode, quota). Onboarding
    // asks for the number again.
  }
}

export function peekSignupPhoneHandoff(): SignupPhoneHandoff | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const result = signupPhoneHandoffSchema.safeParse(JSON.parse(raw));
    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

export function clearSignupPhoneHandoff(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear if storage is unavailable.
  }
}
